import React, { useState } from 'react';
import BasePOIModal from './BasePOIModal';
import { MeasurementSelector } from './components/MeasurementSelector';
import { NotesAndPhotos } from './components/NotesAndPhotos';
import { WidthClearance } from './components/WidthClearance';

const INTERSECTION_TYPES = [
  { value: 'four_way', label: '4-Ways Intersection' },
  { value: 't_intersection', label: 'T-Intersection' },
  { value: 'roundabout', label: 'Roundabout' },
  { value: '90_left', label: '90° Left Turn' },
  { value: '90_right', label: '90° Right Turn' },
  { value: 'sharp_left', label: 'Sharp Left Turn (over 90°)' },
  { value: 'sharp_right', label: 'Sharp Right Turn (over 90°)' }
];

const TRAFFIC_CONTROLS = [
  { value: 'traffic_signal', label: 'Traffic Signal' },
  { value: 'stop_signs', label: 'Stop Signs' },
  { value: 'yield_signs', label: 'Yield Signs' }
];

export const IntersectionPOIModal = ({ type, poiInfo, onClose, onSave, onBack }) => {
  const [formData, setFormData] = useState({
    intersectionType: '',
    trafficControl: '',
    width: { feet: '', inches: '', meters: '' },
    touchLevel: null,
    measurementType: 'touch',
    customHeight: { feet: '', inches: '', meters: '' },
    notes: '',
    photos: []
  });

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {};
    if (!formData.intersectionType) errors.intersectionType = 'Intersection type is required';
    if (!formData.trafficControl) errors.trafficControl = 'Traffic control type is required';
    if (formData.measurementType === 'touch' && !formData.touchLevel) {
      errors.measurements = 'Touch level is required';
    }
    if (formData.measurementType === 'custom' && 
        !formData.customHeight.feet && 
        !formData.customHeight.meters) {
      errors.measurements = 'Height measurement is required';
    }

    if (Object.keys(errors).length > 0) {
      return { success: false, errors };
    }

    // Prepare measurements array
    const measurements = [];
    if (formData.measurementType === 'touch') {
      measurements.push({
        type: 'touch',
        value: formData.touchLevel,
        unit: 'inches'
      });
    } else {
      if (formData.customHeight.meters) {
        measurements.push({
          type: 'custom',
          value: parseFloat(formData.customHeight.meters),
          unit: 'meters'
        });
      } else {
        measurements.push({
          type: 'custom',
          value: parseInt(formData.customHeight.feet),
          unit: 'feet'
        });
        if (formData.customHeight.inches) {
          measurements.push({
            type: 'custom',
            value: parseInt(formData.customHeight.inches),
            unit: 'inches'
          });
        }
      }
    }

    return onSave({
      measurements,
      notes: formData.notes,
      photos: formData.photos,
      intersectionDetails: {
        type: formData.intersectionType,
        trafficControl: formData.trafficControl
      }
    });
  };

  return (
    <BasePOIModal
      type={type}
      poiInfo={poiInfo}
      onClose={onClose}
      onSave={handleSubmit}
      onBack={onBack}
    >
      <div className="space-y-6">
        <MeasurementSelector
          measurementType={formData.measurementType}
          setMeasurementType={(type) => setFormData(prev => ({ ...prev, measurementType: type }))}
          touchLevel={formData.touchLevel}
          setTouchLevel={(level) => setFormData(prev => ({ ...prev, touchLevel: level }))}
          customHeight={formData.customHeight}
          setCustomHeight={(height) => setFormData(prev => ({ ...prev, customHeight: height }))}
        />

        <WidthClearance 
          width={formData.width}
          setWidth={(width) => setFormData(prev => ({ ...prev, width }))}
        />

        <div>
          <label className="block text-gray-400 mb-2">Intersection Type</label>
          <select
            value={formData.intersectionType}
            onChange={(e) => setFormData(prev => ({ ...prev, intersectionType: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">Select Intersection Type</option>
            {INTERSECTION_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Traffic Control</label>
          <select
            value={formData.trafficControl}
            onChange={(e) => setFormData(prev => ({ ...prev, trafficControl: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">Select Traffic Control</option>
            {TRAFFIC_CONTROLS.map(control => (
              <option key={control.value} value={control.value}>{control.label}</option>
            ))}
          </select>
        </div>

        <NotesAndPhotos
          notes={formData.notes}
          setNotes={(notes) => setFormData(prev => ({ ...prev, notes }))}
          photos={formData.photos}
          setPhotos={(photos) => setFormData(prev => ({ ...prev, photos }))}
        />
      </div>
    </BasePOIModal>
  );
};

export default IntersectionPOIModal;